// @flow
import { getAuthToken, getBidderId } from './user';
import {
    LOAD_RESELLER_TYPE_FAIL,
    LOAD_RESELLER_TYPE_REQUEST,
    LOAD_RESELLER_TYPE_SUCCESS,
    SEND_RESELLER_TYPE_FAIL,
    SEND_RESELLER_TYPE_REQUEST,
    SEND_RESELLER_TYPE_SUCCESS,
} from './actions/reseller';
import type { ActionWithPayload } from '../../types/redux';
import api from '../api/reseller';
import { createSelector } from 'reselect';
import { getDeployment } from './config';
import { handleActions } from 'redux-actions';
import ms from 'ms';
import resellerTypes from '../../enums/resellerTypes';

const REDUX_STORE_TIME = ms('30m');

// reducer
const DEFAULT_STATE = {
    error: false,
    loaded: 0,
    loading: false,
    resellerSelection: undefined,
};

type State = {|
    ...typeof DEFAULT_STATE,
|};

export const reducer = handleActions(
    {
        [LOAD_RESELLER_TYPE_FAIL]: (state: State, action: ActionWithPayload<any>) => ({
            ...state,
            error: action.payload,
            loading: false,
        }),
        [LOAD_RESELLER_TYPE_REQUEST]: (state: State) => ({
            ...state,
            loading: true,
        }),
        [LOAD_RESELLER_TYPE_SUCCESS]: (state: State, action: any) => ({
            ...state,
            loaded: action.meta.actionTime,
            loading: false,
            resellerSelection: action.payload.resellerType,
        }),
        [SEND_RESELLER_TYPE_FAIL]: (state: State, action: ActionWithPayload<{ error: string }>) => ({
            ...state,
            error: action.error,
            loading: false,
        }),
        [SEND_RESELLER_TYPE_REQUEST]: (state: State) => ({
            ...state,
            loading: true,
        }),
        [SEND_RESELLER_TYPE_SUCCESS]: (state: State) => ({
            ...state,
            loading: false,
        }),
    },
    DEFAULT_STATE
);

/* SELECTORS */
const stateSelector = state => state.reseller;

export const getResellerType = createSelector(stateSelector, state => state.resellerSelection);

const isLoading = createSelector(stateSelector, state => state.loading);

const isLoaded = createSelector(stateSelector, state => state.loaded);

const shouldFetchResellerType = createSelector(
    [getResellerType, isLoading, isLoaded],
    (resellerType, loading, loaded) => {
        if (resellerType) {
            const time = Date.now();
            const diff = time - loaded;

            if (diff < REDUX_STORE_TIME) return false;
        }
        return !loading;
    }
);

/* ACTION CREATORS */
const loadResellerType = () => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const bidderId = getBidderId(state);

        dispatch({ type: LOAD_RESELLER_TYPE_REQUEST });

        const response = await api.getResellerType({ authToken, bidderId, deployment });

        return dispatch({
            meta: { actionTime: Date.now() },
            payload: response.payload,
            type: LOAD_RESELLER_TYPE_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: LOAD_RESELLER_TYPE_FAIL,
        });
    }
};

export const fetchResellerTypeIfNeeded = () => async (dispatch: Function, getState: Function) => {
    if (shouldFetchResellerType(getState())) {
        return dispatch(loadResellerType());
    }
    return Promise.resolve();
};

export const sendResellerType = (resellerSelection: number) => async (dispatch: Function, getState: Function) => {
    try {
        dispatch({
            type: SEND_RESELLER_TYPE_REQUEST,
        });
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const bidderId = getBidderId(state);
        const resellerType = resellerSelection ? resellerTypes.RESELLER : resellerTypes.NOT_A_RESELLER;

        const response = await api.postResellerType({
            authToken,
            bidderId,
            deployment,
            resellerType,
        });

        return dispatch({
            payload: response.payload,
            type: SEND_RESELLER_TYPE_SUCCESS,
        });
    } catch (error) {
        return dispatch({
            error: true,
            payload: error.message || error,
            type: SEND_RESELLER_TYPE_FAIL,
        });
    }
};
